import {useEffect, useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {Link, Skeleton, Typography} from '@mui/material';
import {shallowEqual, useSelector} from 'react-redux';
import {Section, Grid, Box} from '@jobseeker/lotus/foundations';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import {
  handleTracking,
  TrackEventUBA,
  openUrl,
  deferTask,
} from '@jobseeker/common-core/utils';
import {useInView} from '@jobseeker/lotus/hooks/observer';
import {parseShowcaseUrl} from '@jobseeker/common-core/utils';
import type {CompanyWorkWithType} from '../types';
import type {AppState, AdsElementType} from '@jobseeker/common-core/types';

const CompanyWorkWith = (props: CompanyWorkWithType) => {
  const [companyInfo, setCompanyInfo] = useState<Array<AdsElementType>>([]);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const {ref, isInView} = useInView({threshold: 0.5});

  const {eventTag, heading, theme, slotId, isLargeXl} = props || {};

  const companies = useSelector(
    (state: AppState) => state.ads.companyWorkWith,
    shallowEqual
  );

  useLoadAds(slotId, setCompanyInfo, {}, false, !companies[0]?.imageUrl);

  const loadedCompanyInfo = companies[0]?.imageUrl ? companies : companyInfo;
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory,
    shallowEqual
  );

  useEffect(() => {
    if (loadedCompanyInfo?.length > 0 && isInView && !hasTrackedView) {
      const favIds = loadedCompanyInfo?.map(comp =>
        comp.companyId ? comp.companyId : parseShowcaseUrl(comp.link)
      );
      TrackEventUBA('companyLogosView', 'view', {
        categoryID: page?.toLowerCase() === 'category' ? categoryId : undefined,
        tagID: page?.toLowerCase() === 'tag' ? categoryId : undefined,
        page: page || '',
        sessionUsername: sessionStorage.getItem('userId') || '',
        source: 'Companies Logo Homepage',
        companyIDSArray: favIds || [],
        companyNamesArray: loadedCompanyInfo.map(emp => emp?.title) || [],
      });
      setHasTrackedView(true);
    }
  }, [loadedCompanyInfo, isInView, hasTrackedView]);

  return (
    <Section textAlign="center">
      <div ref={ref}></div>
      <Typography
        component={'h2'}
        sx={{
          textAlign: 'center',
          fontSize: {
            xxs: theme.typography.pxToRem(20),
            md: theme.typography.pxToRem(26),
          },
          fontWeight: {xxs: 600, md: 400},
          display: heading ? 'block' : 'flex',
          justifyContent: heading ? '' : 'center',
        }}
      >
        {heading ? (
          heading
        ) : (
          <Skeleton
            key={'skeleton-company'}
            animation="wave"
            width={150}
            height={24}
            sx={{
              textAlign: 'center',
            }}
          />
        )}
      </Typography>
      <Grid
        container
        sx={{
          justifyContent: 'space-evenly',
          maxWidth: {xxs: '100%', md: '90%'},
          borderTop: `${theme.spacing(0.125)} solid #d9d9d9`,
          borderBottom: `${theme.spacing(0.125)} solid #d9d9d9`,
          marginTop: {xxs: theme.spacing(3.125), md: theme.spacing(5.625)},
          marginLeft: {
            sm: theme.spacing(5),
            md: theme.spacing(5.625),
            lg: theme.spacing(9.375),
            xl: theme.spacing(9.375),
          },
        }}
        data-testid="company-work-with"
      >
        {loadedCompanyInfo[0]?.imageUrl
          ? loadedCompanyInfo.map((company, index) => (
              <Grid key={`companyworkwith-${index}`} item xxs={4} sm={1} md={2}>
                <Link
                  margin={`${theme.spacing(3.125)} 0`}
                  padding={`0 ${theme.spacing(2.5)}`}
                  boxSizing="border-box"
                  width="16%"
                  href={company['link']}
                  target="_blank"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    const url = company['link'];
                    if (url) {
                      openUrl(url, '_blank');
                    }
                    deferTask(() => {
                      handleTracking(eventTag, {
                        event_category: 'engagement',
                        Label: company['title'],
                        Value: `${index}`,
                      });
                      TrackEventUBA('companyLogosClick', 'click', {
                        categoryID:
                          page?.toLowerCase() === 'category'
                            ? categoryId
                            : undefined,
                        tagID:
                          page?.toLowerCase() === 'tag'
                            ? categoryId
                            : undefined,
                        companyID:
                          company?.companyId ||
                          parseShowcaseUrl(company?.link) ||
                          '',
                        companyName: company?.title || '',
                        source: 'Companies Logo Homepage',
                        page: 'HomePage',
                        sessionUsername: sessionStorage.getItem('userId') || '',
                      });
                    });
                  }}
                >
                  <Box>
                    <Image
                      src={company['imageUrl']}
                      alt={company['title']}
                      width={isLargeXl ? 150 : 100}
                      height={isLargeXl ? 120 : 80}
                      placeholder="blur"
                      blurDataURL={company['imageUrl']}
                      style={{
                        maxWidth: theme.spacing(25),
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Link>
              </Grid>
            ))
          : [...new Array(7)].map((p, i) => (
              <Skeleton
                key={`workwith-company-skeleton-${i}`}
                animation="wave"
                sx={{
                  width: {xxs: 100, md: 150},
                  height: {xxs: 80, md: 160},
                }}
              />
            ))}
      </Grid>
    </Section>
  );
};
export default CompanyWorkWith;
